// src/components/Auth/Signup.js
import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Lock, Mail, Phone, User, UserPlus, X, FileText } from "lucide-react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAuthApi } from "../../hooks/useAuthApi";
import {
  signupDefaultValues,
  signupSchema,
} from "../../utils/validationSchemas";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { useToast } from "../../hooks/use-toast"; // Fixed import path

const Signup = ({ onToggle, loading, setLoading }) => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const { toast } = useToast(); // Make sure this hook is working
  const {
    register: registerUser,
    loading: apiLoading,
    error: apiError,
  } = useAuthApi();

  const [consentOpen, setConsentOpen] = useState(false);
  const [consentViewed, setConsentViewed] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(signupSchema),
    defaultValues: signupDefaultValues,
  });

  // Watch password field for confirm password validation — commented out (fields hidden for now)
  // const password = watch("password");
  const agreeToDisclaimer = watch("agreeToDisclaimer");

  // Clear API errors when form changes
  React.useEffect(() => {
    if (apiError) {
      clearErrors("root");
    }
  }, [apiError, clearErrors]);

  const onSubmit = async (formData) => {
    setLoading(true);

    try {
      const apiData = {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        password: formData.password,
        repassword: formData.confirmPassword,
        phone: formData.phone,
      };

      const response = await registerUser(apiData);

      // Check for success based on your API response structure
      if (response?.status === 201 || response?.success) {
        // Show success toast
        toast({
          title: "Account created successfully!",
          description:
            response.data?.message ||
            "Welcome to GEETH365. Your account has been created.",
          duration: 3000,
        });

        console.log("Toast should have been shown"); // Debug log

        // Switch to login tab after a short delay
        setTimeout(() => {
          onToggle("login", { phone: formData.phone });
        }, 1500);
      } else {
        // Handle unexpected response
        setError("root", {
          type: "server",
          message: "Registration completed but unexpected response.",
        });
      }
    } catch (error) {
      console.error("Registration error:", error); // Debug log

      // Handle specific error cases
      if (error.response?.data?.errors) {
        // Handle field-specific errors from API
        const fieldErrors = error.response.data.errors;
        Object.keys(fieldErrors).forEach((field) => {
          setError(field, {
            type: "server",
            message: fieldErrors[field][0],
          });
        });
      } else if (error.response?.data?.detail) {
        setError("root", {
          type: "server",
          message: error.response.data.detail,
        });
      } else if (error.response?.data?.message) {
        setError("root", {
          type: "server",
          message: error.response.data.message,
        });
      } else {
        setError("root", {
          type: "server",
          message: error.message || "Registration failed. Please try again.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Prevent non-numeric input and limit to 10 digits for phone field
  const handlePhoneInput = (e) => {
    let value = e.target.value.replace(/[^\d]/g, "");
    // Limit to 10 digits
    if (value.length > 10) {
      value = value.slice(0, 10);
    }
    e.target.value = value;
  };

  const isLoading = loading || apiLoading;

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* ... rest of your form JSX remains the same ... */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label
              htmlFor="signup-first-name"
              className="flex items-center space-x-2"
            >
              <User className="w-4 h-4" />
              <span>First Name</span>
            </Label>
            <Input
              id="signup-first-name"
              type="text"
              placeholder="Enter first name"
              className="mt-1"
              {...register("first_name")}
              disabled={isLoading}
            />
            {errors.first_name && (
              <p className="text-red-500 text-sm mt-1">
                {errors.first_name.message}
              </p>
            )}
          </div>

          <div>
            <Label
              htmlFor="signup-last-name"
              className="flex items-center space-x-2"
            >
              <User className="w-4 h-4" />
              <span>Last Name</span>
            </Label>
            <Input
              id="signup-last-name"
              type="text"
              placeholder="Enter last name"
              className="mt-1"
              {...register("last_name")}
              disabled={isLoading}
            />
            {errors.last_name && (
              <p className="text-red-500 text-sm mt-1">
                {errors.last_name.message}
              </p>
            )}
          </div>
        </div>

        {/* Email field — commented out for now */}
        {/* <div>
          <Label htmlFor="signup-email" className="flex items-center space-x-2">
            <Mail className="w-4 h-4" />
            <span>Email</span>
          </Label>
          <Input
            id="signup-email"
            type="email"
            placeholder="Enter your email"
            className="mt-1"
            {...register("email")}
            disabled={isLoading}
          />
          {errors.email && (
            <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
          )}
        </div> */}

        <div>
          <Label htmlFor="signup-phone" className="flex items-center space-x-2">
            <Phone className="w-4 h-4" />
            <span>Phone Number</span>
          </Label>
          <Input
            id="signup-phone"
            type="tel"
            placeholder="Enter 10-digit phone number"
            className="mt-1"
            {...register("phone")}
            onInput={handlePhoneInput}
            inputMode="numeric"
            maxLength={10}
            disabled={isLoading}
          />
          {errors.phone && (
            <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
          )}
          {!errors.phone && (
            <p className="text-gray-500 text-xs mt-1">
              Enter 10-digit number only
            </p>
          )}
        </div>

        {/* Password field — commented out for now */}
        {/* <div>
          <Label
            htmlFor="signup-password"
            className="flex items-center space-x-2"
          >
            <Lock className="w-4 h-4" />
            <span>Password</span>
          </Label>
          <Input
            id="signup-password"
            type="password"
            placeholder="Create a password (min 8 characters)"
            className="mt-1"
            {...register("password")}
            disabled={isLoading}
          />
          {errors.password && (
            <p className="text-red-500 text-sm mt-1">
              {errors.password.message}
            </p>
          )}
        </div> */}

        {/* Confirm Password field — commented out for now */}
        {/* <div>
          <Label
            htmlFor="signup-confirm-password"
            className="flex items-center space-x-2"
          >
            <Lock className="w-4 h-4" />
            <span>Confirm Password</span>
          </Label>
          <Input
            id="signup-confirm-password"
            type="password"
            placeholder="Confirm your password"
            className="mt-1"
            {...register("confirmPassword")}
            disabled={isLoading}
          />
          {errors.confirmPassword && (
            <p className="text-red-500 text-sm mt-1">
              {errors.confirmPassword.message}
            </p>
          )}
        </div> */}

        {/* Health Data Consent */}
        <div className="space-y-1.5">
          <div className="flex items-start space-x-3 px-1">
            <div className="flex items-center h-5 mt-0.5">
              <input
                type="checkbox"
                id="agreeToDisclaimer"
                className={`w-4 h-4 text-orange-600 bg-white border-gray-300 rounded focus:ring-orange-500 focus:ring-2 ${consentViewed ? "cursor-pointer" : "cursor-not-allowed opacity-40"}`}
                {...register("agreeToDisclaimer")}
                disabled={isLoading || !consentViewed}
              />
            </div>
            <Label
              htmlFor="agreeToDisclaimer"
              className="text-xs text-gray-700 select-none leading-relaxed"
            >
              I agree to the{" "}
              <button
                type="button"
                onClick={() => { setConsentOpen(true); setConsentViewed(true); }}
                className="text-orange-600 hover:text-orange-700 underline font-medium"
              >
                Digital Informed Consent Declaration
              </button>
              {!consentViewed && (
                <span className="ml-1 text-orange-500 font-medium">← please read first</span>
              )}
            </Label>
          </div>
          {errors.agreeToDisclaimer && (
            <p className="text-red-500 text-sm mt-1 px-1">
              {errors.agreeToDisclaimer.message}
            </p>
          )}
        </div>

        {(errors.root || apiError) && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">
              {errors.root?.message || apiError}
            </p>
          </div>
        )}

        <Button
          type="submit"
          className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-2.5"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Creating Account...
            </>
          ) : (
            <>
              <UserPlus className="w-4 h-4 mr-2" />
              Sign Up Free
            </>
          )}
        </Button>
      </form>

      <div className="text-center">
        <p className="text-sm text-gray-600">
          Already have an account?{" "}
          <button
            type="button"
            onClick={() => onToggle("login")}
            className="text-orange-600 hover:text-orange-700 font-medium"
            disabled={isLoading}
          >
            Login
          </button>
        </p>
      </div>

      {/* Consent Modal */}
      {consentOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
              <div className="flex items-center space-x-2">
                <FileText className="w-5 h-5 text-orange-600" />
                <p className="text-sm font-semibold text-gray-900">
                  Digital Informed Consent Declaration
                </p>
              </div>
              <button
                type="button"
                onClick={() => setConsentOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="overflow-y-auto px-5 py-4 text-xs text-gray-600 space-y-3 leading-relaxed flex-1">
              <p>
                By selecting this checkbox and completing the registration process, I hereby
                provide my free, specific, informed, explicit, and unambiguous electronic
                consent to the collection, recording, storage, organization, structuring,
                consultation, retrieval, analysis, use, processing, pseudonymisation,
                de-identification, aggregation, and other legally permissible handling of my
                health-related information by the platform and its authorized affiliates,
                service providers, research partners, and analytical entities for the purposes
                specified herein.
              </p>
              <p>
                The categories of health-related information that may be collected include:
                disease classification, morbidity profile, diagnostic category, diagnostic
                patterns, symptomatology, clinical history, laboratory investigations,
                radiological findings, other diagnostic tests conducted, test utilization
                trends, treatment pathways, therapeutic interventions, medication exposure,
                prescription patterns, disease progression indicators, outcome indicators, and
                financial burden associated with disease management.
              </p>
              <p>
                Such information may be processed for: disease burden assessment, clinical
                pattern analysis, diagnostic trend evaluation, therapeutic and medication
                development support, treatment optimization studies, health economic
                evaluation, pricing and affordability analysis, actuarial modelling,
                forecasting, prognosis modelling, public health analytics, and future
                population-level or intergenerational disease trend assessments.
              </p>
              <p>
                Personally unnecessary data elements — including GPS location history, SMS
                content, unrelated communication data, and other non-essential device-level
                personal information — shall not be collected under this consent unless
                separately disclosed and independently consented to.
              </p>
              <p>
                Your health-related information may be processed in identifiable,
                pseudonymised, de-identified, anonymised, or aggregated form as reasonably
                required for lawful operational, analytical, research, commercial, statistical,
                regulatory, or compliance purposes, subject to applicable legal, ethical, and
                technical safeguards.
              </p>
              <p>
                Your affirmative action through checkbox acceptance and account registration
                constitutes your valid electronic consent and acceptance of this Health Data
                Processing Agreement. No separate physical signature is required unless
                mandated by applicable law or institutional ethics policy.
              </p>
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={() => setConsentOpen(false)}
                className="text-sm text-gray-600 hover:text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  setValue("agreeToDisclaimer", true);
                  setConsentOpen(false);
                }}
                className="text-sm bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                I Agree &amp; Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Signup;
